import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import useBreakpoint from '../hooks/useBreakpoint';
import { toastMobile, pageMobile, TAP_TARGET } from '../mobileStyles';

const MENSAJES_URL = 'https://lifesosperun8n-n8n.o4xul0.easypanel.host/webhook/get-mensajes';
const RECORDATORIOS_URL = 'https://lifesosperun8n-n8n.o4xul0.easypanel.host/webhook/get-recordatorios';
const ATENDER_URL = 'https://lifesosperun8n-n8n.o4xul0.easypanel.host/webhook/atender-recordatorio';

const REFRESH_MS = 60 * 1000;

const PRIORITY_STYLE = {
  alta:  { background: 'rgba(184,59,59,0.10)', color: '#B83B3B', border: 'rgba(184,59,59,0.25)' },
  media: { background: 'rgba(217,120,66,0.12)', color: '#C26A2C', border: 'rgba(217,120,66,0.3)' },
  baja:  { background: 'rgba(74,124,89,0.10)', color: '#4A7C59', border: 'rgba(74,124,89,0.25)' },
};

const TIPO_STYLE = {
  recordatorio: { background: 'rgba(107,28,42,0.10)', color: '#6B1C2A' },
  importante:   { background: 'rgba(201,169,110,0.18)', color: '#A08040' },
  pregunta:     { background: 'rgba(74,111,165,0.12)', color: '#3E6594' },
};

const GROUP_PALETTE = [
  { bg: 'rgba(107,28,42,0.10)',   fg: '#6B1C2A' },
  { bg: 'rgba(201,169,110,0.18)', fg: '#A08040' },
  { bg: 'rgba(74,124,89,0.12)',   fg: '#4A7C59' },
  { bg: 'rgba(74,111,165,0.12)',  fg: '#3E6594' },
  { bg: 'rgba(184,123,67,0.12)',  fg: '#B87B43' },
  { bg: 'rgba(125,86,148,0.12)',  fg: '#7D5694' },
  { bg: 'rgba(64,118,124,0.12)',  fg: '#3E7A80' },
];

function groupColor(name) {
  if (!name) return GROUP_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return GROUP_PALETTE[hash % GROUP_PALETTE.length];
}

// Display timestamps in Lima time (UTC-2 per spec).
function fmtLimaTime(isoStr) {
  if (!isoStr) return '—';
  const d = new Date(isoStr);
  if (Number.isNaN(d.getTime())) return '—';
  const shifted = new Date(d.getTime() - 2 * 60 * 60 * 1000);
  return shifted.toLocaleString('es-PE', {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function isUndef(v) {
  return v == null || v === 'undefined' || v === '';
}

async function postJson(url, body) {
  // eslint-disable-next-line no-console
  console.log('[wa] POST →', url, body);
  let res;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {}),
    });
  } catch (networkErr) {
    // eslint-disable-next-line no-console
    console.error('[wa] network error for', url, networkErr);
    throw new Error(`Network error: ${networkErr.message || 'fetch failed'}`);
  }
  const text = await res.text();
  // eslint-disable-next-line no-console
  console.log('[wa] ←', url, res.status, text || '(empty body)');
  if (!res.ok) {
    // Surface as much of the response body as possible so we see what n8n said.
    const snippet = (text || '').slice(0, 300);
    throw new Error(`HTTP ${res.status}${snippet ? ` — ${snippet}` : ''}`);
  }
  if (!text) return null;
  try { return JSON.parse(text); } catch { return null; }
}

export default function WhatsAppMessages() {
  const { isMobile, isCompact } = useBreakpoint();
  const [recordatorios, setRecordatorios] = useState([]);
  const [mensajes, setMensajes] = useState([]);
  const [loadingR, setLoadingR] = useState(true);
  const [loadingM, setLoadingM] = useState(true);
  const [errorR, setErrorR] = useState(null);
  const [errorM, setErrorM] = useState(null);
  const [filterGroup, setFilterGroup] = useState('all');
  const [recFilterGroup, setRecFilterGroup] = useState('all');
  const [recFilterTipo, setRecFilterTipo] = useState('all');
  const [collapsed, setCollapsed] = useState({});
  const [marking, setMarking] = useState({});
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  // Multi-select for bulk "Atender". Set of recordatorio ids the user has
  // checked. Cleared after a successful bulk action or explicit Cancelar.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkMarking, setBulkMarking] = useState(false);

  function showToast(message, type = 'success') {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ message, type });
    toastTimer.current = setTimeout(() => setToast(null), 3500);
  }

  const fetchRecordatorios = useCallback(async () => {
    try {
      const data = await postJson(RECORDATORIOS_URL, {});
      const list = Array.isArray(data) ? data : (data?.recordatorios || []);
      // The get-recordatorios webhook is the source of truth. It is expected
      // to filter atendido = false server-side, so whatever it returns is the
      // current set for every user (Jose, Ximena, …). Just replace state.
      setRecordatorios(list.filter(r => !isUndef(r?.grupo_nombre) && !isUndef(r?.resumen)));
      setErrorR(null);
    } catch (e) {
      setErrorR(e.message || 'Error al cargar recordatorios');
    } finally {
      setLoadingR(false);
    }
  }, []);

  const fetchMensajes = useCallback(async () => {
    try {
      const data = await postJson(MENSAJES_URL, {});
      const list = Array.isArray(data) ? data : (data?.mensajes || []);
      setMensajes(list.filter(m => !isUndef(m?.remitente_nombre) && !isUndef(m?.mensaje)));
      setErrorM(null);
    } catch (e) {
      setErrorM(e.message || 'Error al cargar mensajes');
    } finally {
      setLoadingM(false);
    }
  }, []);

  useEffect(() => {
    fetchRecordatorios();
    fetchMensajes();
    const id = setInterval(() => {
      fetchRecordatorios();
      fetchMensajes();
    }, REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchRecordatorios, fetchMensajes]);

  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  // Drop selected ids that are no longer present in the latest fetch result
  // (e.g. another user attended them) so the floating bar count stays honest.
  useEffect(() => {
    setSelectedIds(prev => {
      if (prev.size === 0) return prev;
      const live = new Set(recordatorios.map(r => r.id).filter(id => id != null));
      let changed = false;
      const next = new Set();
      for (const id of prev) {
        if (live.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [recordatorios]);

  async function atender(rec) {
    // eslint-disable-next-line no-console
    console.log('[wa] atender click', { id: rec?.id, rec });
    if (rec?.id == null) {
      // eslint-disable-next-line no-console
      console.warn('[wa] atender aborted: recordatorio has no id', rec);
      showToast('Error: recordatorio sin id', 'error');
      return;
    }
    if (marking[rec.id]) {
      // eslint-disable-next-line no-console
      console.log('[wa] atender already in-flight for', rec.id, '— ignoring click');
      return;
    }
    setMarking(prev => ({ ...prev, [rec.id]: true }));
    try {
      // The webhook accepts a single uniform schema { ids: [...] } so the
      // n8n side has one query path for both individual and bulk actions.
      const response = await postJson(ATENDER_URL, { ids: [rec.id] });
      // eslint-disable-next-line no-console
      console.log('[wa] atender webhook OK for', rec.id, '→', response);
      await fetchRecordatorios();
      // Drop from any in-progress multi-select so the floating bar stays accurate.
      setSelectedIds(prev => {
        if (!prev.has(rec.id)) return prev;
        const n = new Set(prev); n.delete(rec.id); return n;
      });
      showToast('Recordatorio atendido ✓');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[wa] atender failed for', rec.id, e);
      showToast('Error al atender: ' + (e.message || 'no se pudo atender'), 'error');
    } finally {
      setMarking(prev => {
        const n = { ...prev };
        delete n[rec.id];
        return n;
      });
    }
  }

  function toggleSelect(id) {
    if (id == null) return;
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }

  function clearSelection() {
    setSelectedIds(new Set());
  }

  // Apply a "Seleccionar …" action against the currently visible list (the
  // group-filtered + tipo-filtered + priority-sorted recordatorios). Operates
  // on sortedRecordatorios so it respects every active filter.
  function selectByTipo(action) {
    if (action === 'clear') {
      clearSelection();
      return;
    }
    const matches = sortedRecordatorios.filter(r => {
      if (r?.id == null) return false;
      if (action === 'all') return true;
      return (r.tipo || '').trim().toLowerCase() === action;
    });
    setSelectedIds(new Set(matches.map(r => r.id)));
  }

  async function atenderSeleccionados() {
    const ids = Array.from(selectedIds);
    // eslint-disable-next-line no-console
    console.log('[wa] atender seleccionados', ids);
    if (ids.length === 0 || bulkMarking) return;
    setBulkMarking(true);
    try {
      const response = await postJson(ATENDER_URL, { ids });
      // eslint-disable-next-line no-console
      console.log('[wa] bulk atender OK →', response);
      await fetchRecordatorios();
      clearSelection();
      showToast(`${ids.length} recordatorio${ids.length === 1 ? '' : 's'} atendido${ids.length === 1 ? '' : 's'} ✓`);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[wa] bulk atender failed', e);
      showToast('Error al atender: ' + (e.message || 'no se pudo atender'), 'error');
    } finally {
      setBulkMarking(false);
    }
  }

  // ── Mensajes derived state ──
  const groups = useMemo(() => {
    const map = new Map();
    for (const m of mensajes) {
      const key = m.grupo_nombre || '—';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(m);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => new Date(b.timestamp || b.created_at).getTime() - new Date(a.timestamp || a.created_at).getTime());
    }
    return Array.from(map.entries()).sort((a, b) => {
      const ta = new Date(a[1][0]?.timestamp || a[1][0]?.created_at || 0).getTime();
      const tb = new Date(b[1][0]?.timestamp || b[1][0]?.created_at || 0).getTime();
      return tb - ta;
    });
  }, [mensajes]);

  const visibleGroups = useMemo(() => {
    if (filterGroup === 'all') return groups;
    return groups.filter(([name]) => name === filterGroup);
  }, [groups, filterGroup]);

  const recGroupOptions = useMemo(() => {
    const set = new Set();
    for (const r of recordatorios) if (r?.grupo_nombre) set.add(r.grupo_nombre);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [recordatorios]);

  // Recordatorios pre-filtered by group only — used both to populate the
  // tipo pills (so counts reflect the visible group) and to apply the tipo
  // filter for the final list.
  const recordatoriosForGroup = useMemo(() => (
    recFilterGroup === 'all'
      ? recordatorios
      : recordatorios.filter(r => r.grupo_nombre === recFilterGroup)
  ), [recordatorios, recFilterGroup]);

  // Dynamic tipo pills built from the data. Keys are lowercased/trimmed so
  // "Importante" and "importante " collapse onto the same pill; the label
  // shown is the first capitalized form encountered. Sorted by count desc,
  // then alphabetically. Filter compares against the normalized key so it
  // stays case/whitespace-insensitive too.
  const recTipoOptions = useMemo(() => {
    const counts = new Map(); // key -> { label, count }
    for (const r of recordatoriosForGroup) {
      const raw = (r?.tipo == null ? '' : String(r.tipo)).trim();
      if (!raw) continue;
      const key = raw.toLowerCase();
      const existing = counts.get(key);
      if (existing) {
        existing.count += 1;
      } else {
        counts.set(key, {
          key,
          label: raw.charAt(0).toUpperCase() + raw.slice(1),
          count: 1,
        });
      }
    }
    return Array.from(counts.values()).sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.label.localeCompare(b.label);
    });
  }, [recordatoriosForGroup]);

  // If the current tipo filter disappears (group switch hides every entry
  // of that tipo), fall back to "all" instead of showing an empty list.
  useEffect(() => {
    if (recFilterTipo === 'all') return;
    if (!recTipoOptions.some(o => o.key === recFilterTipo)) setRecFilterTipo('all');
  }, [recFilterTipo, recTipoOptions]);

  const sortedRecordatorios = useMemo(() => {
    const order = { alta: 0, media: 1, baja: 2 };
    const tipoFiltered = recFilterTipo === 'all'
      ? recordatoriosForGroup
      : recordatoriosForGroup.filter(r => ((r?.tipo || '').trim().toLowerCase()) === recFilterTipo);
    return [...tipoFiltered].sort((a, b) => {
      const pa = order[a.prioridad] ?? 9;
      const pb = order[b.prioridad] ?? 9;
      if (pa !== pb) return pa - pb;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [recordatoriosForGroup, recFilterTipo]);

  function toggleGroup(name) {
    setCollapsed(prev => ({ ...prev, [name]: !prev[name] }));
  }

  const totalMensajes = mensajes.length;

  return (
    <div style={isMobile ? {
      ...styles.container, ...pageMobile,
      // Leave room for the docked bulk-action bar.
      ...(selectedIds.size > 0 ? { paddingBottom: 'calc(130px + env(safe-area-inset-bottom))' } : null),
    } : styles.container}>
      <style>{`
        .wa-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          align-items: stretch;
          height: calc(100vh - 140px);
          min-height: 500px;
        }
        .wa-col {
          display: flex;
          flex-direction: column;
          min-height: 0;
          height: 100%;
        }
        .wa-col-body {
          flex: 1;
          overflow-y: auto;
          min-height: 0;
        }
        @media (max-width: 900px) {
          .wa-grid {
            grid-template-columns: 1fr;
            height: auto;
          }
          .wa-col {
            height: auto;
          }
          .wa-col-body {
            max-height: 70vh;
          }
        }
        @media (max-width: 1023.98px) {
          .wa-grid {
            grid-template-columns: 1fr;
            height: auto;
            min-height: 0;
          }
          .wa-col {
            height: auto;
          }
          .wa-col-body {
            max-height: 70vh;
          }
          .wa-col-recordatorios { order: 1; }
          .wa-col-mensajes { order: 2; }
        }
        @media (max-width: 639.98px) {
          .wa-grid { gap: 14px; }
          .wa-col-body { max-height: none; }
        }
      `}</style>

      <header style={styles.header}>
        <div>
          <h2 style={styles.title}>WhatsApp Messages</h2>
          <p style={styles.subtitle}>Recordatorios y mensajes en tiempo real desde n8n</p>
        </div>
      </header>

      <div className="wa-grid">
        {/* RECORDATORIOS */}
        <section className={isCompact ? 'wa-col wa-col-recordatorios' : 'wa-col'} style={styles.card}>
          <div style={isMobile ? { ...styles.cardHeader, ...styles.cardHeaderMobile } : styles.cardHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={styles.waBadge}>WA</span>
              <h3 style={styles.cardTitle}>Recordatorios</h3>
              <span style={styles.countPill}>{sortedRecordatorios.length}</span>
            </div>
            <div style={isMobile ? styles.cardToolsMobile : { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <select
                style={isCompact ? { ...styles.filterSelect, ...styles.filterSelectTouch } : styles.filterSelect}
                value={recFilterGroup}
                onChange={e => setRecFilterGroup(e.target.value)}
              >
                <option value="all">Todos los grupos</option>
                {recGroupOptions.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
              <button style={isCompact ? { ...styles.refreshBtn, ...styles.tapTouch } : styles.refreshBtn} onClick={fetchRecordatorios} disabled={loadingR}>
                {loadingR ? 'Actualizando…' : '↻ Actualizar'}
              </button>
            </div>
          </div>

          {errorR && <div style={styles.errorBox}>Error: {errorR}</div>}

          {(recTipoOptions.length > 0 || recFilterTipo !== 'all') && (
            <div style={styles.tipoPillRow}>
              <button
                type="button"
                onClick={() => setRecFilterTipo('all')}
                style={{
                  ...styles.tipoPill,
                  ...(recFilterTipo === 'all' ? styles.tipoPillActive : {}),
                  ...(isCompact ? styles.tapTouch : {}),
                }}
              >
                Todos
                <span style={{
                  ...styles.tipoPillCount,
                  ...(recFilterTipo === 'all' ? styles.tipoPillCountActive : {}),
                }}>{recordatoriosForGroup.length}</span>
              </button>
              {recTipoOptions.map(opt => {
                const active = recFilterTipo === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setRecFilterTipo(opt.key)}
                    style={{
                      ...styles.tipoPill,
                      ...(active ? styles.tipoPillActive : {}),
                      ...(isCompact ? styles.tapTouch : {}),
                    }}
                  >
                    {opt.label}
                    <span style={{
                      ...styles.tipoPillCount,
                      ...(active ? styles.tipoPillCountActive : {}),
                    }}>{opt.count}</span>
                  </button>
                );
              })}
            </div>
          )}

          {sortedRecordatorios.length > 0 && (
            <div style={styles.selectAllRow}>
              <select
                style={isCompact ? { ...styles.selectAllDropdown, ...styles.filterSelectTouch } : styles.selectAllDropdown}
                value=""
                onChange={(e) => {
                  const v = e.target.value;
                  if (!v) return;
                  selectByTipo(v);
                  e.target.value = '';
                }}
                aria-label="Seleccionar recordatorios visibles"
              >
                <option value="" disabled>Seleccionar…</option>
                <option value="all">Seleccionar todos ({sortedRecordatorios.length})</option>
                {recTipoOptions.map(opt => {
                  const visibleCount = sortedRecordatorios.filter(
                    r => (r.tipo || '').trim().toLowerCase() === opt.key,
                  ).length;
                  if (visibleCount === 0) return null;
                  return (
                    <option key={opt.key} value={opt.key}>
                      Seleccionar {opt.label.toLowerCase()}s ({visibleCount})
                    </option>
                  );
                })}
                <option value="clear">Deseleccionar todos</option>
              </select>
              {selectedIds.size > 0 && (
                <span style={styles.selectAllHint}>
                  {selectedIds.size} marcado{selectedIds.size === 1 ? '' : 's'}
                </span>
              )}
            </div>
          )}

          <div className="wa-col-body">
            {loadingR && recordatorios.length === 0 ? (
              <div style={styles.emptyMsg}>Cargando recordatorios…</div>
            ) : sortedRecordatorios.length === 0 ? (
              <div style={styles.emptyMsg}>No hay recordatorios pendientes.</div>
            ) : (
              <div style={isMobile ? { ...styles.recList, ...styles.recListMobile } : styles.recList}>
                {sortedRecordatorios.map(rec => {
                  const tipo = (rec.tipo || '').toLowerCase();
                  const prio = (rec.prioridad || '').toLowerCase();
                  const tipoStyle = TIPO_STYLE[tipo] || TIPO_STYLE.recordatorio;
                  const prioStyle = PRIORITY_STYLE[prio] || PRIORITY_STYLE.media;
                  const isSelected = rec.id != null && selectedIds.has(rec.id);
                  return (
                    <article
                      key={rec.id}
                      style={{
                        ...styles.recCard,
                        borderLeft: `4px solid ${prioStyle.color}`,
                        ...(isSelected ? styles.recCardSelected : {}),
                      }}
                    >
                      <div style={styles.recTopRow}>
                        <label
                          style={isCompact ? { ...styles.recCheckboxWrap, ...styles.recCheckboxWrapTouch } : styles.recCheckboxWrap}
                          title={isSelected ? 'Deseleccionar' : 'Seleccionar para atender en lote'}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelect(rec.id)}
                            disabled={rec.id == null || bulkMarking}
                            style={isCompact ? { ...styles.recCheckbox, ...styles.recCheckboxTouch } : styles.recCheckbox}
                          />
                        </label>
                        <div style={isCompact ? { ...styles.recBadgeRow, alignSelf: 'center' } : styles.recBadgeRow}>
                          <span style={{ ...styles.tipoBadge, background: tipoStyle.background, color: tipoStyle.color }}>
                            {rec.tipo || '—'}
                          </span>
                          <span style={{
                            ...styles.prioBadge,
                            background: prioStyle.background,
                            color: prioStyle.color,
                            borderColor: prioStyle.border,
                          }}>
                            {rec.prioridad || '—'}
                          </span>
                        </div>
                      </div>
                      <p style={isMobile ? { ...styles.recResumen, ...styles.recResumenMobile } : styles.recResumen}>{rec.resumen}</p>
                      <div style={isMobile ? { ...styles.recMeta, ...styles.recMetaMobile } : styles.recMeta}>
                        <span><strong>{rec.remitente || '—'}</strong></span>
                        <span style={styles.metaDot}>·</span>
                        <span style={styles.recGroup}>{rec.grupo_nombre || '—'}</span>
                      </div>
                      <div style={styles.recFooter}>
                        <span style={styles.recTime}>{fmtLimaTime(rec.created_at)}</span>
                        <button
                          type="button"
                          style={{
                            ...styles.attendBtn,
                            ...(marking[rec.id] ? { opacity: 0.7, cursor: 'wait' } : {}),
                            ...(isCompact ? styles.attendBtnTouch : {}),
                          }}
                          onClick={() => atender(rec)}
                          disabled={!!marking[rec.id]}
                          aria-busy={!!marking[rec.id]}
                        >
                          {marking[rec.id] ? 'Marcando…' : '✓ Atendido'}
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* MENSAJES RECIENTES */}
        <section className={isCompact ? 'wa-col wa-col-mensajes' : 'wa-col'} style={styles.card}>
          <div style={isMobile ? { ...styles.cardHeader, ...styles.cardHeaderMobile } : styles.cardHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <h3 style={styles.cardTitle}>Mensajes Recientes</h3>
              <span style={styles.countPill}>{totalMensajes}</span>
            </div>
            <div style={isMobile ? styles.cardToolsMobile : { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <select
                style={isCompact ? { ...styles.filterSelect, ...styles.filterSelectTouch } : styles.filterSelect}
                value={filterGroup}
                onChange={e => setFilterGroup(e.target.value)}
              >
                <option value="all">Todos los grupos</option>
                {groups.map(([name]) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
              <button style={isCompact ? { ...styles.refreshBtn, ...styles.tapTouch } : styles.refreshBtn} onClick={fetchMensajes} disabled={loadingM}>
                {loadingM ? 'Actualizando…' : '↻ Actualizar'}
              </button>
            </div>
          </div>

          {errorM && <div style={styles.errorBox}>Error: {errorM}</div>}

          <div className="wa-col-body">
            {loadingM && mensajes.length === 0 ? (
              <div style={styles.emptyMsg}>Cargando mensajes…</div>
            ) : visibleGroups.length === 0 ? (
              <div style={styles.emptyMsg}>No hay mensajes para mostrar.</div>
            ) : (
              <div>
                {visibleGroups.map(([groupName, items]) => {
                  const color = groupColor(groupName);
                  const isCollapsed = !!collapsed[groupName];
                  return (
                    <div key={groupName} style={styles.groupBlock}>
                      <button
                        type="button"
                        style={isMobile ? { ...styles.groupHeader, ...styles.groupHeaderMobile } : styles.groupHeader}
                        onClick={() => toggleGroup(groupName)}
                      >
                        <span style={styles.groupHeaderLeft}>
                          <span style={{ ...styles.groupBadge, background: color.bg, color: color.fg }}>
                            {groupName}
                          </span>
                          <span style={styles.groupCount}>{items.length}</span>
                        </span>
                        <span style={styles.chev}>{isCollapsed ? '▸' : '▾'}</span>
                      </button>
                      {!isCollapsed && (
                        <div style={styles.msgList}>
                          {items.map(m => (
                            <div key={m.id} style={isMobile ? { ...styles.msgItem, ...styles.msgItemMobile } : styles.msgItem}>
                              <div style={styles.msgTop}>
                                <span style={isMobile ? { ...styles.msgSender, fontSize: 14 } : styles.msgSender}>{m.remitente_nombre}</span>
                                <span style={styles.msgTime}>{fmtLimaTime(m.timestamp || m.created_at)}</span>
                              </div>
                              <p style={isMobile ? { ...styles.msgText, ...styles.msgTextMobile } : styles.msgText}>{m.mensaje}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </div>

      {selectedIds.size > 0 && (
        <div style={isMobile ? { ...styles.bulkBar, ...styles.bulkBarMobile } : styles.bulkBar} role="region" aria-label="Acciones en lote">
          <span style={styles.bulkCount}>
            {selectedIds.size} seleccionado{selectedIds.size === 1 ? '' : 's'}
          </span>
          <div style={isMobile ? { ...styles.bulkActions, ...styles.bulkActionsMobile } : styles.bulkActions}>
            <button
              type="button"
              style={isCompact ? { ...styles.bulkCancelBtn, ...styles.bulkBtnTouch } : styles.bulkCancelBtn}
              onClick={clearSelection}
              disabled={bulkMarking}
            >
              Cancelar
            </button>
            <button
              type="button"
              style={{
                ...styles.bulkAttendBtn,
                ...(bulkMarking ? { opacity: 0.7, cursor: 'wait' } : {}),
                ...(isCompact ? styles.bulkBtnTouch : {}),
              }}
              onClick={atenderSeleccionados}
              disabled={bulkMarking}
              aria-busy={bulkMarking}
            >
              {bulkMarking ? 'Atendiendo…' : '✓ Atender seleccionados'}
            </button>
          </div>
        </div>
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: selectedIds.size > 0 ? 96 : 28, left: '50%', transform: 'translateX(-50%)',
          background: toast.type === 'error' ? '#B83B3B' : '#4A7C59',
          color: '#fff', padding: '10px 24px', borderRadius: 10, fontSize: 13, fontWeight: 600,
          fontFamily: "'DM Sans',sans-serif", zIndex: 9999, boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          ...(isMobile ? {
            ...toastMobile,
            // Keep clear of the (possibly wrapped) bulk-action bar.
            ...(selectedIds.size > 0 ? { bottom: 'calc(130px + env(safe-area-inset-bottom))' } : null),
          } : null),
        }}>{toast.message}</div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '32px 36px', maxWidth: 1400 },
  header: { marginBottom: 20 },
  title: { fontFamily: "'Cormorant Garamond',serif", fontSize: 32, fontWeight: 600, color: 'var(--wine)', margin: 0 },
  subtitle: { fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 },

  card: { background: '#fff', borderRadius: 14, border: '1px solid var(--border)', overflow: 'hidden' },
  cardHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '18px 24px 14px', borderBottom: '1px solid #f3efe9', background: '#FDFBF8',
    gap: 12, flexWrap: 'wrap',
  },
  cardTitle: { fontFamily: "'Cormorant Garamond',serif", fontSize: 20, fontWeight: 600, color: 'var(--wine)', margin: 0 },
  waBadge: { fontSize: 10, fontWeight: 800, color: '#fff', background: '#25D366', padding: '3px 9px', borderRadius: 6, letterSpacing: '0.06em' },
  countPill: { fontSize: 11, fontWeight: 600, color: 'var(--wine)', background: 'rgba(107,28,42,0.08)', padding: '3px 11px', borderRadius: 20 },
  refreshBtn: {
    padding: '7px 14px', borderRadius: 8, border: '1px solid var(--border)', background: '#fff',
    color: 'var(--wine)', fontSize: 12, fontWeight: 600, cursor: 'pointer',
    fontFamily: "'DM Sans',sans-serif", whiteSpace: 'nowrap',
  },
  filterSelect: {
    padding: '7px 11px', borderRadius: 8, border: '1px solid var(--border)', background: '#fff',
    fontSize: 12, fontFamily: "'DM Sans',sans-serif", color: 'var(--text-primary)', outline: 'none',
    minWidth: 180,
  },

  emptyMsg: { padding: '32px 24px', textAlign: 'center', fontSize: 13, color: 'var(--text-light)' },
  errorBox: { padding: '10px 24px', background: 'rgba(184,59,59,0.06)', color: '#B83B3B', fontSize: 12, borderBottom: '1px solid #f3efe9' },

  tipoPillRow: {
    display: 'flex', flexWrap: 'wrap', gap: 8,
    padding: '12px 18px 0',
  },
  selectAllRow: {
    display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
    padding: '10px 18px 0',
  },
  selectAllDropdown: {
    padding: '6px 11px', borderRadius: 8,
    border: '1px solid var(--border)', background: '#fff',
    fontSize: 12, fontFamily: "'DM Sans',sans-serif",
    color: 'var(--text-primary)', outline: 'none',
    cursor: 'pointer', minWidth: 200,
  },
  selectAllHint: {
    fontSize: 11.5, fontWeight: 600, color: 'var(--wine)',
  },
  tipoPill: {
    display: 'inline-flex', alignItems: 'center', gap: 7,
    padding: '5px 11px',
    borderRadius: 999,
    border: '1px solid var(--border)',
    background: '#fff',
    color: 'var(--text-secondary)',
    fontSize: 12, fontWeight: 600,
    fontFamily: "'DM Sans',sans-serif",
    cursor: 'pointer',
    transition: 'background 0.15s, color 0.15s, border-color 0.15s',
  },
  tipoPillActive: {
    background: 'var(--wine)',
    borderColor: 'var(--wine)',
    color: '#fff',
  },
  tipoPillCount: {
    fontSize: 11, fontWeight: 700,
    background: 'rgba(107,28,42,0.08)',
    color: 'var(--wine)',
    padding: '1px 7px',
    borderRadius: 999,
    minWidth: 18,
    textAlign: 'center',
  },
  tipoPillCountActive: {
    background: 'rgba(255,255,255,0.22)',
    color: '#fff',
  },

  /* Recordatorios */
  recList: {
    display: 'flex', flexDirection: 'column', gap: 12,
    padding: 18,
  },
  recCard: {
    background: '#FDFBF8', borderRadius: 10, border: '1px solid var(--border)',
    padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10,
  },
  recCardSelected: {
    borderColor: 'var(--wine)',
    boxShadow: '0 0 0 1px var(--wine) inset, 0 2px 10px rgba(107,28,42,0.08)',
    background: '#FBF6F0',
  },
  recTopRow: {
    display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap',
  },
  recCheckboxWrap: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    padding: 2, marginTop: -2, cursor: 'pointer',
  },
  recCheckbox: {
    width: 16, height: 16, accentColor: '#6B1C2A', cursor: 'pointer', margin: 0,
  },
  recBadgeRow: { display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1, minWidth: 0 },
  tipoBadge: {
    fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 6,
    textTransform: 'uppercase', letterSpacing: '0.05em',
  },
  prioBadge: {
    fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 6,
    textTransform: 'uppercase', letterSpacing: '0.05em', border: '1px solid',
  },
  recResumen: { fontSize: 13.5, color: 'var(--text-primary)', margin: 0, lineHeight: 1.45, fontWeight: 500 },
  recMeta: { display: 'flex', gap: 6, alignItems: 'center', fontSize: 11.5, color: 'var(--text-secondary)', flexWrap: 'wrap' },
  metaDot: { color: 'var(--text-light)' },
  recGroup: { color: 'var(--text-secondary)' },
  recFooter: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginTop: 4, paddingTop: 8, borderTop: '1px dashed #ece6dd',
  },
  recTime: { fontSize: 11, color: 'var(--text-light)' },
  attendBtn: {
    padding: '6px 12px', borderRadius: 6, border: 'none', background: '#4A7C59',
    color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer',
    fontFamily: "'DM Sans',sans-serif", whiteSpace: 'nowrap',
  },

  bulkBar: {
    position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
    background: '#fff', color: 'var(--text-primary)',
    borderRadius: 12, padding: '12px 18px',
    display: 'flex', alignItems: 'center', gap: 18,
    boxShadow: '0 8px 30px rgba(0,0,0,0.18)',
    border: '1px solid var(--border)',
    fontFamily: "'DM Sans',sans-serif",
    zIndex: 9998,
    maxWidth: 'calc(100vw - 32px)',
    flexWrap: 'wrap', justifyContent: 'center',
  },
  bulkCount: {
    fontSize: 13, fontWeight: 700, color: 'var(--wine)',
    whiteSpace: 'nowrap',
  },
  bulkActions: {
    display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
  },
  bulkCancelBtn: {
    padding: '8px 14px', borderRadius: 8,
    border: '1px solid var(--border)', background: '#fff',
    color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600,
    cursor: 'pointer', fontFamily: "'DM Sans',sans-serif",
  },
  bulkAttendBtn: {
    padding: '8px 16px', borderRadius: 8, border: 'none',
    background: 'var(--wine)', color: '#fff',
    fontSize: 12, fontWeight: 700, cursor: 'pointer',
    fontFamily: "'DM Sans',sans-serif", whiteSpace: 'nowrap',
  },

  /* Mensajes */
  groupBlock: { borderTop: '1px solid #f3efe9' },
  groupHeader: {
    width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 24px', background: '#fff', border: 'none', cursor: 'pointer',
    fontFamily: "'DM Sans',sans-serif",
  },
  groupHeaderLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  groupBadge: {
    fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 8,
  },
  groupCount: { fontSize: 11, color: 'var(--text-light)', fontWeight: 600 },
  chev: { fontSize: 12, color: 'var(--text-secondary)' },
  msgList: { display: 'flex', flexDirection: 'column' },
  msgItem: { padding: '10px 24px 10px 36px', borderTop: '1px solid #f8f4ee' },
  msgTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3, gap: 12 },
  msgSender: { fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' },
  msgTime: { fontSize: 11, color: 'var(--text-light)', whiteSpace: 'nowrap' },
  msgText: { fontSize: 13, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5, wordBreak: 'break-word' },

  /* Compact-only additions (merged only when useBreakpoint() says so) */
  tapTouch: { minHeight: TAP_TARGET, boxSizing: 'border-box' },
  cardHeaderMobile: { padding: '14px 16px 12px' },
  cardToolsMobile: { display: 'flex', gap: 8, alignItems: 'stretch', width: '100%' },
  filterSelectTouch: { minHeight: TAP_TARGET, minWidth: 0, flex: 1, fontSize: 16, boxSizing: 'border-box' },
  recListMobile: { padding: 12 },
  recCheckboxWrapTouch: { minWidth: TAP_TARGET, minHeight: TAP_TARGET, margin: '-10px 0 -10px -12px', padding: 0 },
  recCheckboxTouch: { width: 22, height: 22 },
  recResumenMobile: { fontSize: 15 },
  recMetaMobile: { fontSize: 13 },
  attendBtnTouch: { minHeight: TAP_TARGET, padding: '0 16px', fontSize: 14, borderRadius: 8 },
  groupHeaderMobile: { padding: '12px 16px', minHeight: TAP_TARGET },
  msgItemMobile: { padding: '12px 16px 12px 20px' },
  msgTextMobile: { fontSize: 15 },
  bulkBarMobile: {
    left: 0, right: 0, bottom: 0, transform: 'none',
    maxWidth: 'none', width: '100%', boxSizing: 'border-box',
    borderRadius: '14px 14px 0 0', borderLeft: 'none', borderRight: 'none', borderBottom: 'none',
    padding: '12px 16px max(12px, env(safe-area-inset-bottom))',
    gap: 10, justifyContent: 'space-between',
  },
  bulkActionsMobile: { flex: '1 1 100%', flexWrap: 'nowrap' },
  bulkBtnTouch: { minHeight: TAP_TARGET, flex: 1, fontSize: 14 },
};
